/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { createContext, useContext, useEffect, useState } from 'react';
import type ResourceItem from 'interfaces/resource';
import { Permission, Token, TokenContextType } from 'contexts/resource/type';
import fetchPermissions from 'core/utils/fetchPermissions';
import { useSetLocale } from '@refinedev/core';
import fetchOrganization from 'core/utils/fetchOrg';
import io, { Socket } from 'socket.io-client';

import axiosInstance from 'providers/axiosInstance/axiosInstance';
import fetchPrograms from 'core/utils/fetchPrograms';
import { REACT_APP_SOCKET_URL } from 'config';
// import { H } from 'highlight.run';

const OrgIDKey = 'orgId';
const programIdKey = 'programId';

const TokenContext = createContext<TokenContextType>({
  token: undefined,
  setToken: () => {},
  userSessionId: undefined,
  setUserSessionId: () => {},
  crudPermissions: [],
  setCrudPermission: () => {},
  modulePermission: [],
  setModulePermission: () => {},
  userDetails: {},
  setUserDetails: () => {},
  filteredResources: [],
  setFilteredResources: () => {},
  loading: false,
  setLoading: () => {},
  orgId: '',
  setOrgId: () => {},
  programId: '',
  setProgramId: () => {},
  userProgramList: [],
  organizationList: [],
  setOrganizationDropdownList: () => {},
  currentLang: '',
  setCurrentLang: () => {},
  setRoles: () => {},
  roles: [],
  socketInstance: undefined,
  setSocketInstance: () => {},
  paymentPartyName: '',
  setPaymentPartyName: () => {},
  compensationType: '',
  setCompensationType: () => {},
  userType: '',
  setUserType: () => {},
  setRoleList: () => {},
  roleList: [],
  currencyVal: '',
  setCurrencyVal: () => {},
  numberingFormat: '',
  setNumberingFormat: () => {},
});

export function useTokenContext() {
  return useContext(TokenContext);
}

const TokenProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<Token>(undefined);
  const [userSessionId, setUserSessionId] = useState<string>(undefined);
  const [crudPermissions, setCrudPermission] = useState<Permission>([]);
  const [modulePermission, setModulePermission] = useState<Permission>([]);
  const [filteredResources, setFilteredResources] = useState<ResourceItem[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState<any>({});
  const [roles, setRoles] = useState([]);

  const [orgId, setOrgIdState] = useState<string | number>(() => {
    const storedOrgId = localStorage.getItem(OrgIDKey);
    return storedOrgId || '';
  });
  const [programId, setProgramIdState] = useState<string>(() => {
    const storedRoleId = localStorage.getItem(programIdKey);
    return storedRoleId || '';
  });
  const [paymentPartyName, setPaymentPartyName] = useState();
  const [compensationType, setCompensationType] = useState('');
  const [currencyVal, setCurrencyVal] = useState('$');
  const [numberingFormat, setNumberingFormat] = useState('indianaaaa');
  const [userType, setUserType] = useState('');

  const [organizationList, setOrganizationList] = useState<any[]>([]);
  const [userProgramList, setUserProgramList] = useState<any[]>([]);
  const [currentLang, setCurrentLang] = useState('en');
  const changeLanguage = useSetLocale();
  const [socketInstance, setSocketInstance] = useState<Socket | undefined>(
    undefined
  );
  const [roleList, setRoleList] = useState([]);

  // Function to set orgId in both state and localStorage
  const setOrgId = (newOrgId: string) => {
    setOrgIdState(newOrgId);
  };

  // Function to set roleId in both state and localStorage
  const setProgramId = (newRoleId: any) => {
    setProgramIdState(newRoleId);
  };

  const connect = (id, orgid) => {
    const userId = id;
    if (socketInstance) {
      return;
    }

    if (userId && orgid) {
      const newSocket = io(REACT_APP_SOCKET_URL, {
        query: { userId, orgid },
      });
      newSocket.on('connect', () => {});

      newSocket.emit('joinRoom', { roomId: `${userId}_${orgid}` });

      // Set the new socket instance
      setSocketInstance(newSocket);
      console.log('newSocket ', newSocket);
    }
  };
  console.log('socketInstance ', socketInstance);

  const fetchData = async () => {
    setLoading(true);
    axiosInstance.defaults.headers.common = {
      Authorization: `Bearer ${token}`,
      'user-session-id': userSessionId,
    };
    await fetchOrganization(setOrganizationList);
    await fetchPermissions(
      setFilteredResources,
      setCrudPermission,
      setModulePermission,
      setUserDetails,
      changeLanguage,
      setCurrentLang,
      connect,
      currentLang,
      token,
      setOrgId,
      setProgramId,
      userSessionId,
      setRoleList
    );
    await fetchPrograms(setUserProgramList);
    setLoading(false);
  };

  useEffect(() => {
    if (token && userSessionId) {
      fetchData();
    }
  }, [token]);
  useEffect(() => {
    if (!!programId && userProgramList?.length > 0) {
      const selectedProgram = userProgramList?.find(
        (item) => item.iprogram_unique_id === programId
      );
      setPaymentPartyName(selectedProgram?.payment_party_name);
      setCompensationType(selectedProgram?.compensation_type);
      if (selectedProgram?.currency) {
        setCurrencyVal(selectedProgram?.currency);
      }
      if (selectedProgram?.numbering_system) {
        setNumberingFormat(selectedProgram?.numbering_system);
      }
      setUserType(selectedProgram?.user_type);
    }
  }, [programId, userProgramList]);

  return (
    <TokenContext.Provider
      value={{
        token,
        setToken,
        crudPermissions,
        setCrudPermission,
        modulePermission,
        setModulePermission,
        filteredResources,
        setFilteredResources,
        userDetails,
        setUserDetails,
        loading,
        setLoading,
        orgId,
        setOrgId,
        setProgramId,
        programId,
        userProgramList,
        organizationList,
        setOrganizationDropdownList: setOrganizationList,
        currentLang,
        setCurrentLang,
        roles,
        setRoles,
        socketInstance,
        setSocketInstance,
        userSessionId,
        setUserSessionId,
        paymentPartyName,
        setPaymentPartyName,
        compensationType,
        setCompensationType,
        userType,
        setUserType,
        setRoleList,
        roleList,
        currencyVal,
        setCurrencyVal,
        numberingFormat,
        setNumberingFormat,
      }}
    >
      {children}
    </TokenContext.Provider>
  );
};

export default TokenProvider;
